/**
 * @voltxampere/ui — Dot grid renderer (Sprint 12)
 *
 * Per Blueprint §12.1 grid layer + Zenit §12 antipattern rules
 * (no shadow, no fill gradient, sharp 1-px dots). The grid is a
 * thin visual cue, not load-bearing.
 *
 * Sprint 26 R5-VIS-6 — zoom threshold tune (F-V-07 closure).
 * Audit reported the grid was visible on rc-low-pass but invisible
 * on voltage-divider at the SAME zoom-level baseline; the previous
 * 0.25 / 0.5 doubling cliffs were too aggressive at small zooms.
 * New thresholds:
 *   zoom < 0.3  -> hidden (nothing rendered; very-zoomed-out scenes
 *                  do not need a grid carpet)
 *   zoom < 0.6  -> 2x spacing (32px world)
 *   zoom >= 0.6 -> 1x spacing (16px world, canonical)
 *
 * `rect` is the visible world rectangle; the renderer passes it
 * down already snapped to viewport.screenToWorld(canvas corners).
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
export const DEFAULT_GRID_SPACING = 16;
/**
 * Draws a dotted grid covering `rect` (world coordinates). The
 * helper assumes the renderer has already applied the world →
 * screen transform; coordinates passed to ctx are in world space.
 */
export function drawDotGrid(ctx, viewport, rect, theme, baseSpacing = DEFAULT_GRID_SPACING) {
    // Sprint 26 R5-VIS-6 — hide at deep zoom-out so the canvas does
    // not present a grey-dot carpet over schematics that span half
    // the world. Below 0.3 the dot density (even with 4x spacing)
    // is essentially noise rather than a guidance hint.
    if (viewport.zoom < 0.3)
        return;
    let spacing = baseSpacing;
    if (viewport.zoom < 0.6)
        spacing = baseSpacing * 2;
    const startX = Math.floor(rect.x / spacing) * spacing;
    const startY = Math.floor(rect.y / spacing) * spacing;
    const endX = rect.x + rect.w;
    const endY = rect.y + rect.h;
    ctx.save();
    ctx.fillStyle = theme.text.wisp;
    for (let y = startY; y <= endY; y += spacing) {
        for (let x = startX; x <= endX; x += spacing) {
            // 1-world-unit square dot. Renderer's transform turns this
            // into a single screen pixel at zoom=1; sharper at higher
            // zoom is acceptable (the grid is a hint, not a measure).
            ctx.fillRect(x, y, 1, 1);
        }
    }
    ctx.restore();
}
//# sourceMappingURL=grid.js.map